export interface DerivedSource {
  channel: string
  campaign?: string
  raw?: Record<string, string>
}

/** Every fixed channel label the source classifier can emit. */
export const KNOWN_SOURCE_CHANNELS = [
  'Direct',
  'Website',
  'Referral',
  'Organic Search',
  'Google Ads',
  'Instagram',
  'Facebook',
  'LinkedIn',
  'TikTok',
  'YouTube',
  'Pinterest',
  'Reddit',
  'X (Twitter)',
  'ChatGPT',
  'Gemini',
  'Perplexity',
  'Microsoft Copilot',
  'Claude',
  'Google',
  'Bing',
  'Yahoo',
  'Newsletter',
  'Email',
] as const

// Friendly labels for known utm_source values. Falls back to a capitalized
// version of the raw value when not listed.
const UTM_SOURCE_LABELS: Record<string, string> = {
  ig: 'Instagram',
  instagram: 'Instagram',
  'instagram.com': 'Instagram',
  fb: 'Facebook',
  facebook: 'Facebook',
  'facebook.com': 'Facebook',
  meta: 'Facebook',
  google: 'Google',
  'google.com': 'Google',
  bing: 'Bing',
  yahoo: 'Yahoo',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  yt: 'YouTube',
  twitter: 'X (Twitter)',
  x: 'X (Twitter)',
  pinterest: 'Pinterest',
  chatgpt: 'ChatGPT',
  'chatgpt.com': 'ChatGPT',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  copilot: 'Microsoft Copilot',
  claude: 'Claude',
  newsletter: 'Newsletter',
  email: 'Email',
}

// Google click IDs are opaque, so do not validate their exact format. This
// minimum only rejects empty, placeholder, and visibly truncated values such
// as "0AA" that cannot support a credible ad-click attribution.
const MIN_GOOGLE_CLICK_ID_LENGTH = 8

// Referrer hostnames that map to a social channel. Host is compared with the
// leading "www." stripped.
const SOCIAL_HOSTS: Array<{ test: (host: string) => boolean; channel: string }> = [
  { test: (h) => h.endsWith('instagram.com'), channel: 'Instagram' },
  { test: (h) => h.endsWith('facebook.com') || h === 'fb.com' || h.endsWith('.fb.com') || h.endsWith('fb.me'), channel: 'Facebook' },
  { test: (h) => h.endsWith('linkedin.com') || h === 'lnkd.in', channel: 'LinkedIn' },
  { test: (h) => h.endsWith('tiktok.com'), channel: 'TikTok' },
  { test: (h) => h.endsWith('youtube.com') || h === 'youtu.be', channel: 'YouTube' },
  { test: (h) => h.endsWith('pinterest.com') || h === 'pin.it', channel: 'Pinterest' },
  { test: (h) => h.endsWith('reddit.com'), channel: 'Reddit' },
  { test: (h) => h.endsWith('twitter.com') || h === 'x.com' || h === 't.co', channel: 'X (Twitter)' },
  { test: (h) => h === 'chatgpt.com' || h.endsWith('.chatgpt.com'), channel: 'ChatGPT' },
]

// Returns true when the host is `domain` or one of its subdomains.
function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`)
}

// AI assistant referrer hosts. Checked before isSearchHost so a Gemini referral
// is not misread as Organic Search.
const AI_ASSISTANT_HOSTS: Array<{ test: (host: string) => boolean; channel: string }> = [
  { test: (h) => hostMatches(h, 'chatgpt.com') || hostMatches(h, 'chat.openai.com'), channel: 'ChatGPT' },
  { test: (h) => hostMatches(h, 'gemini.google.com') || hostMatches(h, 'bard.google.com'), channel: 'Gemini' },
  { test: (h) => hostMatches(h, 'perplexity.ai'), channel: 'Perplexity' },
  { test: (h) => hostMatches(h, 'copilot.microsoft.com'), channel: 'Microsoft Copilot' },
  { test: (h) => hostMatches(h, 'claude.ai'), channel: 'Claude' },
]

// Returns true when the host is a search engine. Anchored on the registrable
// label so "tagassistant.google.com" (GTM debug) is NOT treated as search.
function isSearchHost(host: string): boolean {
  return (
    /^google\.[a-z.]+$/.test(host) ||
    host.endsWith('bing.com') ||
    host === 'yahoo.com' ||
    host.endsWith('search.yahoo.com') ||
    host.endsWith('duckduckgo.com') ||
    host.endsWith('ecosia.org') ||
    host.endsWith('baidu.com') ||
    /(^|\.)yandex\./.test(host) ||
    host.endsWith('search.brave.com')
  )
}

function safeHost(url: string | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return ''
  }
}

// Reads a query param from the first URL that contains it.
function queryParam(name: string, urls: Array<string | undefined>): string | undefined {
  for (const url of urls) {
    if (!url) continue
    try {
      const value = new URL(url).searchParams.get(name)
      if (value) return value
    } catch {
      // ignore unparseable URLs
    }
  }
  return undefined
}

function labelUtmSource(source: string): string {
  const key = source.trim().toLowerCase()
  return UTM_SOURCE_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

function isCredibleGoogleClickId(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length >= MIN_GOOGLE_CLICK_ID_LENGTH
}

/**
 * Derives a human-readable source channel from the raw JSON attribution string
 * stored on Inquiry/InquiryLite.
 *
 * The form embeds itself on the org's own website, so click IDs and UTM params
 * frequently live inside the `referrer_url` / `landing_page_url` query strings
 * rather than as top-level keys — those are sniffed out here.
 *
 * Derivation priority:
 * 1. Google Ads — a credible gclid/gbraid/wbraid is present (top-level or in a URL)
 * 2. utm_source present (top-level or in a URL) → mapped/capitalized label.
 *    A whitespace-only value counts as absent, so the ladder continues below.
 * 3. fbclid present (top-level or in a URL) → "Facebook"
 * 4. referrer host is a known social network → that network
 * 5. referrer host is a known AI assistant → that assistant
 * 6. referrer host is a search engine → "Organic Search"
 * 7. referrer is an external site (≠ landing host) → "Referral"
 * 8. referrer is the org's own site (= landing host, or no landing) → "Website"
 * 9. no referrer → "Direct"
 */
export function deriveSource(sourceJson: string | undefined): DerivedSource {
  if (!sourceJson) {
    return { channel: 'Direct' }
  }

  let raw: Record<string, string>
  try {
    raw = JSON.parse(sourceJson)
  } catch {
    return { channel: 'Direct' }
  }

  const urls = [raw.referrer_url, raw.landing_page_url]
  const campaign = raw.utm_campaign || queryParam('utm_campaign', urls) || undefined
  const base = { campaign, raw }

  const isGoogleAds =
    isCredibleGoogleClickId(raw.gclid) ||
    isCredibleGoogleClickId(queryParam('gclid', urls)) ||
    isCredibleGoogleClickId(queryParam('gbraid', urls)) ||
    isCredibleGoogleClickId(queryParam('wbraid', urls))
  if (isGoogleAds) {
    return { channel: 'Google Ads', ...base }
  }

  const utmSource = raw.utm_source?.trim() || queryParam('utm_source', urls)?.trim() || ''
  if (utmSource) {
    return { channel: labelUtmSource(utmSource), ...base }
  }

  if (raw.fbclid || queryParam('fbclid', urls)) {
    return { channel: 'Facebook', ...base }
  }

  const referrerHost = safeHost(raw.referrer_url)
  if (referrerHost) {
    const social = SOCIAL_HOSTS.find((s) => s.test(referrerHost))
    if (social) {
      return { channel: social.channel, ...base }
    }
    const aiAssistant = AI_ASSISTANT_HOSTS.find((a) => a.test(referrerHost))
    if (aiAssistant) {
      return { channel: aiAssistant.channel, ...base }
    }
    if (isSearchHost(referrerHost)) {
      return { channel: 'Organic Search', ...base }
    }

    const landingHost = safeHost(raw.landing_page_url)
    if (landingHost && referrerHost !== landingHost) {
      return { channel: 'Referral', ...base }
    }
    return { channel: 'Website', ...base }
  }

  return { channel: 'Direct', ...base }
}
